import { BrokerAvatar } from "@/components/brokers/broker-avatar";
import { BuyerProfileFields } from "@/components/listings/buyer-profile-fields";
import {
  InquiryIntakeFields,
  type InquiryForm,
} from "@/components/intake/inquiry-intake-fields";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Textarea } from "@/components/ui/textarea";
import Link from "next/link";
import type { useListingInquiryDialog } from "./use-listing-inquiry-dialog";

export function InquiryFormPanel({
  model,
}: {
  model: ReturnType<typeof useListingInquiryDialog>;
}) {
  const {
    t,
    user,
    loaded,
    senderError,
    delegated,
    inlineName,
    setInlineName,
    inlinePhone,
    setInlinePhone,
    message,
    setMessage,
    handleTextareaFocus,
    handleTextareaBlur,
    brokerInquiryBlocked,
    isLoggedIn,
    needsName,
    needsPhone,
    needsBuyerProfile,
    buyerProfile,
    setBuyerProfile,
    listingIntakeForm,
    intakeResponses,
    setIntakeResponses,
    profileName,
    error,
    sendMessageMutation,
    handleFormSubmit,
    isOwnerOrAgent,
  } = model;
  return (
    <div className="flex flex-col gap-4">
      {isLoggedIn && needsName && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-name" className="text-sm font-medium">
            Your name <span className="text-destructive">*</span>
          </label>
          <Input
            id="inquiry-name"
            value={inlineName}
            onChange={(e) => setInlineName(e.target.value)}
            placeholder="Full name"
            autoComplete="name"
          />
        </div>
      )}

      {isLoggedIn && needsPhone && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor="inquiry-phone" className="text-sm font-medium">
            Phone{" "}
            <span className="text-xs font-normal text-muted-foreground">
              (optional)
            </span>
          </label>
          <PhoneInput
            rawValue={inlinePhone}
            onChange={({ countryCode, phoneNumber }) => {
              setInlinePhone(`${countryCode} ${phoneNumber}`);
            }}
          />
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label htmlFor="inquiry-message" className="text-sm font-medium">
          Message to the broker
        </label>
        <Textarea
          id="inquiry-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onFocus={handleTextareaFocus}
          onBlur={handleTextareaBlur}
          className="min-h-[100px]"
          disabled={brokerInquiryBlocked}
        />
      </div>

      {brokerInquiryBlocked && (
        <Alert variant="destructive">
          <AlertDescription>
            {t("saas.inquiryRestrictions.brokerNotAccepting")}
          </AlertDescription>
        </Alert>
      )}

      {isLoggedIn && needsBuyerProfile && (
        <BuyerProfileFields data={buyerProfile} onChange={setBuyerProfile} />
      )}

      {listingIntakeForm ? (
        <InquiryIntakeFields
          form={listingIntakeForm as InquiryForm}
          responses={intakeResponses}
          onChange={setIntakeResponses}
        />
      ) : null}

      {isLoggedIn && user && !needsName && (
        <div className="flex flex-col gap-2">
          <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Sending as
          </div>
          <div className="border border-border rounded-lg p-3 flex items-center gap-3">
            <BrokerAvatar
              id={user.id}
              name={profileName}
              image={user.image}
              isVerified={false}
              showVerified={false}
              size="sm"
            />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{profileName}</div>
              <div className="text-xs text-muted-foreground truncate">
                {user.email}
              </div>
            </div>
            {!delegated && (
              <Button variant="ghost" size="sm" className="h-8 px-2" asChild>
                <Link href="/my-profile" aria-label="Edit your profile">
                  <Icon icon="Pencil2" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      )}

      {senderError && (
        <p role="alert">
          Inquiry sender is unavailable. Choose an authorized workspace and try
          again.
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}

      <Button
        className="h-12 w-full bg-foreground text-background hover:bg-foreground/90"
        onClick={handleFormSubmit}
        disabled={
          !loaded ||
          sendMessageMutation.isPending ||
          isOwnerOrAgent ||
          !message.trim() ||
          (needsName && !inlineName.trim()) ||
          brokerInquiryBlocked
        }
      >
        {isLoggedIn ? (
          <>
            <Icon icon="Send" />
            {t("saas.listing.sidebar.contactLoggedIn.form.submit")}
          </>
        ) : (
          "Continue"
        )}
      </Button>

      <p className="text-xs text-muted-foreground text-center leading-relaxed text-pretty">
        Your message goes directly to the broker. Most replies arrive within 24
        hours.
      </p>
    </div>
  );
}
